import { FormEvent, MouseEvent } from 'react';
import styled from 'styled-components';

import { FileDropzone, FileDropzoneContainer } from 'app/theme/FileDropzone';
import { ModalContent } from 'app/theme/Modal';
import { fileUploader } from 'app/translations/dictionary';
import { FileCard } from 'pkg/components/FileUploader/FileUploaderContent/FileCard';
import { FileUploaderHeader } from 'pkg/components/FileUploader/FileUploaderContent/FileUploaderHeader';
import { FileUploaderSubmitButton } from 'pkg/components/FileUploader/FileUploaderContent/FileUploaderSubmitButton';
import { OpenUploaderLink } from 'pkg/components/FileUploader/FileUploaderContent/OpenUploaderLink';
import { useAsyncDropzone } from 'pkg/components/FileUploader/FileUploaderContent/useAsyncDropzone';
import { UploadedFile } from 'pkg/components/FileUploader/UploadedFile';

interface FileUploaderContentProps {
  files: UploadedFile[];
  addFiles: (newFiles: File[]) => Promise<void>;
  removeFile: (fileToRemove: UploadedFile) => void;
  clearFiles: () => void;
  onSubmit: () => void;
  onFileRejection: (rejectedFileTypes: string[]) => void;
  loading: boolean;
  disabled: boolean;
  accept?: string | string[];
  children?: JSX.Element;
}

const Form = styled.form`
  height: 100%;
`;

const Content = styled(ModalContent)`
  &&&& {
    padding: 3rem 2rem 3rem 2rem;
    display: flex;
    height: 100%;
    flex-direction: column;
    justify-content: space-between;
  }
`;

const DeleteFilesButton = styled.button`
  border: none;
  margin: 0;
  outline: none;
  cursor: pointer;
  background-color: inherit;
  text-align: right;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.primary};
  grid-column: 1 / -1;
  padding-bottom: 1rem;
  place-self: end;

  &:hover {
    text-decoration: underline;
  }
`;

export function FileUploaderContent({
  files,
  addFiles,
  accept,
  removeFile,
  clearFiles,
  onFileRejection,
  disabled,
  onSubmit,
  loading,
  children,
}: FileUploaderContentProps): JSX.Element {
  const { getRootProps, getInputProps } = useAsyncDropzone({ onDrop: addFiles, onFileRejection, accept });
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onSubmit();
  };

  const handleClearFiles = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    clearFiles();
  };

  const rootProps = getRootProps();

  return (
    <Form onSubmit={handleSubmit}>
      <Content>
        <FileUploaderHeader />
        <FileDropzoneContainer>
          {children}
          <FileDropzone {...rootProps} data-testid="files">
            <input {...getInputProps()} />
            {files.map((uploadedFile) => (
              <FileCard key={uploadedFile.id} onRemove={() => removeFile(uploadedFile)} uploadedFile={uploadedFile} />
            ))}
            {files.length > 0 && (
              <DeleteFilesButton onClick={handleClearFiles} type="button">
                {fileUploader.deleteAll}
              </DeleteFilesButton>
            )}
          </FileDropzone>
        </FileDropzoneContainer>
        <OpenUploaderLink disabled={disabled} onClick={rootProps.onClick} />
        <FileUploaderSubmitButton isLoading={loading || disabled} />
      </Content>
    </Form>
  );
}
